/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  UserInfo,
  MarketInfo,
  DivisionInfo,
  OrganizationInfo,
  SaveQueryModal,
  SavedQueriesSidebar,
} from '../../components';
import { getToken } from '../../utils/token';
import { config } from '../../config';
import { savedQueriesService, SavedQuery } from '../../services/savedQueries.service';
import './ChatPage.css';

export const ChatPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());
  const [copiedToolId, setCopiedToolId] = useState<string | null>(null);
  const [selectedDatacenter, setSelectedDatacenter] = useState<string>('aws-us-west-2');
  const selectedDatacenterRef = useRef<string>('aws-us-west-2');
  const lastProcessedQuery = useRef<string | null>(null);

  // Saved queries state
  const [savedQueries, setSavedQueries] = useState<SavedQuery[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [queryToSave, setQueryToSave] = useState<string>(''); // SQL query to save
  const [originalQuestion, setOriginalQuestion] = useState<string>(''); // Natural language question
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingSavedQueries, setLoadingSavedQueries] = useState(false);
  const [lastSubmittedQuery, setLastSubmittedQuery] = useState<string>('');

  // Keep ref in sync with state
  useEffect(() => {
    selectedDatacenterRef.current = selectedDatacenter;
  }, [selectedDatacenter]);

  const { messages, sendMessage, status, error } = useChat({
    transport: new DefaultChatTransport({
      api: `${config.ADMIN_API_URL}/api/admin/v2/agent/bedrock/stream`,
      headers: () => ({
        Authorization: `Bearer ${getToken()}`,
        'x-c2fo-dc': selectedDatacenterRef.current,
      }),
    }),
  });

  // Load saved queries on mount
  useEffect(() => {
    const loadSavedQueries = async () => {
      try {
        setLoadingSavedQueries(true);
        const token = getToken();
        const queries = await savedQueriesService.getAll(token);
        setSavedQueries(queries);
      } catch (error) {
        console.error('Failed to load saved queries:', error);
      } finally {
        setLoadingSavedQueries(false);
      }
    };

    loadSavedQueries();
  }, []);

  // Auto-submit query from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q');

    // Submit if we have a query, status is ready, and we haven't processed this exact query yet
    if (query && status === 'ready' && query !== lastProcessedQuery.current) {
      lastProcessedQuery.current = query;
      setInput(query);
      sendMessage({ text: query });
      setLastSubmittedQuery(query);
      // Clear the query parameter after submitting
      navigate('/chat', { replace: true });
    }
  }, [location.search, status, sendMessage, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && status === 'ready') {
      const query = input.trim();
      sendMessage({ text: query });
      setLastSubmittedQuery(query);
      setInput('');
    }
  };

  const handleSaveQuery = (sqlQuery: string, naturalLanguageQuery?: string) => {
    setQueryToSave(sqlQuery);
    setOriginalQuestion(naturalLanguageQuery || '');
    setShowSaveModal(true);
  };

  const handleSaveQueryConfirm = async (name?: string, description?: string) => {
    try {
      const token = getToken();
      const savedQuery = await savedQueriesService.create(token, {
        query: queryToSave, // SQL query
        datacenter: selectedDatacenter,
        originalQuestion: originalQuestion || undefined, // Natural language question
        name,
        description,
      });
      setSavedQueries([savedQuery, ...savedQueries]);
      setShowSaveModal(false);
      setQueryToSave('');
      setOriginalQuestion('');
    } catch (error) {
      console.error('Failed to save query:', error);
      alert('Failed to save query. Please try again.');
    }
  };

  const handleLoadQuery = (query: SavedQuery) => {
    setSelectedDatacenter(query.datacenter);
    setSidebarOpen(false);

    // If we have the original question, send it to the AI for execution
    // Otherwise, just put the SQL query in the input field for reference
    const queryText = query.originalQuestion || `Execute this SQL: ${query.query}`;
    setInput(queryText);

    // Mark as executed
    savedQueriesService.markAsExecuted(getToken(), query.id).catch((error) => {
      console.error('Failed to mark query as executed:', error);
    });
  };

  const handleExecuteQuery = async (query: SavedQuery) => {
    try {
      const token = getToken();
      console.log(`Executing saved query: ${query.name}`);

      const result = await savedQueriesService.execute(token, query.id);

      if (result.success) {
        console.log(`Query executed successfully: ${result.rowCount} rows in ${result.executionTimeMs}ms`);
        console.log('Results:', result.results);

        // Update the saved queries list to reflect the new lastExecutedAt
        const updatedQueries = await savedQueriesService.getAll(token);
        setSavedQueries(updatedQueries);

        // Show success message with results
        alert(
          `Query executed successfully!\n\n${result.rowCount} rows returned in ${result.executionTimeMs}ms\n\nResults are in the console.`,
        );
      } else {
        console.error('Query execution failed:', result.error);
        alert(`Query execution failed:\n\n${result.error}`);
      }
    } catch (error) {
      console.error('Failed to execute query:', error);
      alert('Failed to execute query. Please try again.');
    }
  };

  const handleDeleteQuery = async (id: string) => {
    try {
      const token = getToken();
      await savedQueriesService.delete(token, id);
      setSavedQueries(savedQueries.filter((q) => q.id !== id));
    } catch (error) {
      console.error('Failed to delete query:', error);
      alert('Failed to delete query. Please try again.');
    }
  };

  return (
    <>
      <div className="chat-container">
        <div className="chat-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <h1 className="text-2xl font-bold">Data Analyst Assistant</h1>
              <Link
                to="/redshift-chat"
                style={{
                  fontSize: '14px',
                  color: '#0066cc',
                  textDecoration: 'none',
                  padding: '4px 12px',
                  border: '1px solid #0066cc',
                  borderRadius: '4px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#0066cc';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#0066cc';
                }}
              >
                → Redshift Chat
              </Link>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="datacenter-select" style={{ fontSize: '14px', fontWeight: 500, color: '#666' }}>
                Datacenter:
              </label>
              <select
                id="datacenter-select"
                value={selectedDatacenter}
                onChange={(e) => setSelectedDatacenter(e.target.value)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="aws-us-west-2">AWS US West 2</option>
                <option value="gcp-us-west1">GCP US West 1</option>
                <option value="aws-ap-south-1">AWS AP South 1 (India)</option>
                <option value="gcp-europe-west3">GCP Europe West 3</option>
                <option value="aws-cn-north-1">AWS CN North 1 (China)</option>
              </select>
            </div>
          </div>
          <p className="text-sm">
            I currently have access to C2FO US DB. I can answer questions about core C2FO entities like users, orgs,
            takers markets, and divisions.
          </p>
        </div>

        <div className="chat-messages">
          {messages.length === 0 && (
            <div className="empty-state">
              <p>Start a conversation by typing a message below.</p>
              <p>BETA: Double check your SQL and results before trusting the AI results.</p>
            </div>
          )}
          {messages.map((message) => (
            <div key={message.id} className={`message message-${message.role}`}>
              <div className="message-role">
                {message.role === 'user' ? 'You' : 'Assistant'}
                {/* Display timing metadata if available */}
                {message.metadata && (message.metadata as { duration_ms?: number }).duration_ms && (
                  <span
                    className="message-timing"
                    style={{
                      marginLeft: '8px',
                      fontSize: '11px',
                      color: '#666',
                      fontWeight: 'normal',
                      backgroundColor: '#f0f0f0',
                      padding: '2px 6px',
                      borderRadius: '3px',
                    }}
                  >
                    ⚡ {((message.metadata as { duration_ms: number }).duration_ms / 1000).toFixed(2)}s
                  </span>
                )}
              </div>
              <div className="message-content">
                {message.parts.map((part, index) => {
                  if (part.type === 'text') {
                    return <span key={index}>{part.text}</span>;
                  }

                  // Handle tool calls - AI SDK uses 'tool-invocation' or 'tool-*' types
                  if (
                    part.type === 'tool-invocation' ||
                    (part.type.startsWith('tool-') && 'state' in part && 'input' in part)
                  ) {
                    const toolPart = part as {
                      toolCallId: string;
                      toolName: string;
                      input: unknown;
                      result?: unknown;
                      state?: string;
                    };
                    const toolId = `${message.id}-tool-${index}`;
                    const isExpanded = expandedTools.has(toolId);
                    const toggleTool = () => {
                      setExpandedTools((prev) => {
                        const next = new Set(prev);
                        if (next.has(toolId)) {
                          next.delete(toolId);
                        } else {
                          next.add(toolId);
                        }

                        return next;
                      });
                    };

                    // Special handling for ClarifyIntent - extract and display the question prominently
                    const isClarifyIntent = toolPart.toolName === 'ClarifyIntent' || part.type === 'tool-ClarifyIntent';

                    if (isClarifyIntent && toolPart.state === 'result' && (toolPart.result as any)?.question) {
                      return (
                        <div key={index} className="clarification-message">
                          <div className="clarification-icon">❓</div>
                          <div className="clarification-content">
                            <div className="clarification-question">{(toolPart.result as any).question}</div>
                            {(toolPart.result as any).context && (
                              <div className="clarification-context">{(toolPart.result as any).context}</div>
                            )}
                          </div>
                        </div>
                      );
                    }

                    // Special handling for Generative UI tools - render specialized components
                    // Check both toolName and type (AI SDK uses both patterns)
                    const isDisplayUserInfo =
                      toolPart.toolName === 'DisplayUserInfo' || part.type === 'tool-DisplayUserInfo';
                    const isDisplayMarketInfo =
                      toolPart.toolName === 'DisplayMarketInfo' || part.type === 'tool-DisplayMarketInfo';
                    const isDisplayDivisionInfo =
                      toolPart.toolName === 'DisplayDivisionInfo' || part.type === 'tool-DisplayDivisionInfo';
                    const isDisplayOrganizationInfo =
                      toolPart.toolName === 'DisplayOrganizationInfo' || part.type === 'tool-DisplayOrganizationInfo';

                    if (isDisplayUserInfo) {
                      if (toolPart.state === 'input-available') {
                        return (
                          <div key={index} className="generative-ui-container">
                            <div className="user-info-loading">Loading user information...</div>
                          </div>
                        );
                      }

                      if (toolPart.state === 'output-available' && toolPart.output) {
                        return (
                          <div key={index} className="generative-ui-container">
                            <UserInfo {...(toolPart.result as any)} />
                          </div>
                        );
                      }

                      if (toolPart.state === 'error') {
                        return (
                          <div key={index} className="generative-ui-container">
                            <div className="user-info-error">
                              Error loading user information: {String(toolPart.result)}
                            </div>
                          </div>
                        );
                      }
                    }

                    if (isDisplayMarketInfo) {
                      if (toolPart.state === 'input-available') {
                        return (
                          <div key={index} className="generative-ui-container">
                            <div className="user-info-loading">Loading market information...</div>
                          </div>
                        );
                      }

                      if (toolPart.state === 'output-available' && toolPart.output) {
                        return (
                          <div key={index} className="generative-ui-container">
                            <MarketInfo {...toolPart.output} />
                          </div>
                        );
                      }

                      if (toolPart.state === 'output-error') {
                        return (
                          <div key={index} className="generative-ui-container">
                            <div className="user-info-error">
                              Error loading market information: {toolPart.errorText}
                            </div>
                          </div>
                        );
                      }
                    }

                    if (isDisplayDivisionInfo) {
                      if (toolPart.state === 'input-available') {
                        return (
                          <div key={index} className="generative-ui-container">
                            <div className="user-info-loading">Loading division information...</div>
                          </div>
                        );
                      }

                      if (toolPart.state === 'output-available' && toolPart.output) {
                        return (
                          <div key={index} className="generative-ui-container">
                            <DivisionInfo {...toolPart.output} />
                          </div>
                        );
                      }

                      if (toolPart.state === 'output-error') {
                        return (
                          <div key={index} className="generative-ui-container">
                            <div className="user-info-error">
                              Error loading division information: {toolPart.errorText}
                            </div>
                          </div>
                        );
                      }
                    }

                    if (isDisplayOrganizationInfo) {
                      if (toolPart.state === 'input-available') {
                        return (
                          <div key={index} className="generative-ui-container">
                            <div className="user-info-loading">Loading organization information...</div>
                          </div>
                        );
                      }

                      if (toolPart.state === 'output-available' && toolPart.output) {
                        return (
                          <div key={index} className="generative-ui-container">
                            <OrganizationInfo {...toolPart.output} />
                          </div>
                        );
                      }

                      if (toolPart.state === 'output-error') {
                        return (
                          <div key={index} className="generative-ui-container">
                            <div className="user-info-error">
                              Error loading organization information: {toolPart.errorText}
                            </div>
                          </div>
                        );
                      }
                    }

                    // Check if this is a SQL-related tool
                    const isSqlTool =
                      toolPart.type === 'tool-ExecuteSQLWithRepair' || toolPart.type === 'tool-ExecuteSQL';
                    // toolPart.type === 'tool-BuildSQL' ||
                    // toolPart.type === 'tool-ValidateSQL';

                    // Debug: log SQL tool parts to see available properties
                    if (isSqlTool) {
                      console.log(
                        'SQL Tool Part:',
                        toolPart.type,
                        toolPart.toolName,
                        'State:',
                        toolPart.state,
                        'Keys:',
                        Object.keys(toolPart),
                      );
                    }

                    // Extract SQL from tool input or output
                    const getSqlQuery = () => {
                      // Check output for SQL first (ExecuteSQLWithRepair returns SQL in output)
                      if (toolPart.output?.sql) return toolPart.output.sql;

                      // Check for completed input (input-available state)
                      if (toolPart.input?.sql) return toolPart.input.sql;

                      // Check for streaming input text (during input-delta)
                      if (toolPart.inputText) {
                        try {
                          // Try to parse the input text as JSON to extract SQL
                          const parsed = JSON.parse(toolPart.inputText);
                          if (parsed.sql) return parsed.sql;
                        } catch (e) {
                          // If not valid JSON yet, check if it contains SQL
                          if (toolPart.inputText.includes('"sql"')) {
                            // Extract SQL from partial JSON
                            const match = toolPart.inputText.match(/"sql"\s*:\s*"([^"]*)"/);
                            if (match) return match[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
                          }
                        }
                      }

                      return null;
                    };

                    const sqlQuery = isSqlTool ? getSqlQuery() : null;

                    // Determine status badge
                    const getStatusInfo = () => {
                      if (toolPart.errorText) return { label: 'Error', className: 'status-error' };
                      if (toolPart.output) return { label: 'Completed', className: 'status-completed' };
                      if (toolPart.state === 'input-available')
                        return { label: 'Running', className: 'status-running' };
                      return { label: 'Pending', className: 'status-pending' };
                    };

                    const statusInfo = getStatusInfo();

                    return (
                      <div key={index} className="tool-call">
                        <button
                          type="button"
                          className="tool-call-header"
                          onClick={toggleTool}
                          aria-label={`Toggle ${toolCall.name} tool details`}
                        >
                          <div className="tool-call-title">
                            <svg
                              className="tool-icon"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="3"></circle>
                              <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3m15.364 6.364l-4.243-4.243m-4.242 0l-4.243 4.243m0-8.485l4.243 4.243m4.242 0l4.243-4.243"></path>
                            </svg>
                            <span className="tool-name">{toolPart.toolName || 'Tool Call'}</span>
                            <span className={`tool-status ${statusInfo.className}`}>{statusInfo.label}</span>
                          </div>
                        </button>

                        {/* Always show SQL query for SQL tools */}
                        {isSqlTool && sqlQuery && (
                          <div
                            className="sql-preview"
                            style={{
                              marginTop: '8px',
                              padding: '12px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  color: '#666',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                }}
                              >
                                SQL Query
                              </span>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(sqlQuery);
                                    const toolId = `${message.id}-tool-${index}`;
                                    setCopiedToolId(toolId);
                                    setTimeout(() => setCopiedToolId(null), 2000);
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    backgroundColor:
                                      copiedToolId === `${message.id}-tool-${index}` ? '#e8f5e9' : '#fff',
                                    border:
                                      copiedToolId === `${message.id}-tool-${index}`
                                        ? '1px solid #4caf50'
                                        : '1px solid #d0d0d0',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    color: copiedToolId === `${message.id}-tool-${index}` ? '#2e7d32' : '#333',
                                    transition: 'all 0.2s ease',
                                  }}
                                  type="button"
                                >
                                  {copiedToolId === `${message.id}-tool-${index}` ? '✓ Copied!' : '📋 Copy'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Find the original user message for this conversation
                                    const userMessage = messages.find((m) => m.role === 'user' && m.id < message.id);
                                    // Extract text from message parts
                                    const messageText =
                                      userMessage?.parts
                                        .filter((p: { type: string; text?: string }) => p.type === 'text')
                                        .map((p: { type: string; text?: string }) => p.text)
                                        .join('') || lastSubmittedQuery;
                                    handleSaveQuery(sqlQuery, messageText);
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    backgroundColor: '#fff',
                                    border: '1px solid #d0d0d0',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    color: '#333',
                                    transition: 'all 0.2s ease',
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f0f7ff';
                                    e.currentTarget.style.borderColor = '#007bff';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                    e.currentTarget.style.borderColor = '#d0d0d0';
                                  }}
                                  type="button"
                                >
                                  💾 Save
                                </button>
                              </div>
                            </div>
                            <pre
                              style={{
                                margin: 0,
                                padding: '8px',
                                backgroundColor: '#fff',
                                borderRadius: '3px',
                                fontSize: '13px',
                                lineHeight: '1.5',
                                overflowX: 'auto',
                                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                                color: '#2d3748',
                              }}
                            >
                              {sqlQuery}
                            </pre>
                          </div>
                        )}

                        {isExpanded && (
                          <div className="tool-call-content">
                            {toolPart.input && Object.keys(toolPart.input).length > 0 && (
                              <div className="tool-call-section">
                                <div className="tool-call-label">PARAMETERS</div>
                                <pre className="tool-call-code">{JSON.stringify(toolPart.input, null, 2)}</pre>
                              </div>
                            )}
                            {toolPart.output && (
                              <div className="tool-call-section">
                                <div className="tool-call-label">RESULT</div>
                                <pre className="tool-call-code">{JSON.stringify(toolPart.output, null, 2)}</pre>
                              </div>
                            )}
                            {toolPart.errorText && (
                              <div className="tool-call-section tool-call-error">
                                <div className="tool-call-label">ERROR</div>
                                <div className="tool-call-code">{toolPart.errorText}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return null;
                })}
              </div>
            </div>
          ))}
          {status === 'submitted' && messages[messages.length - 1]?.role === 'user' && (
            <div className="message message-assistant">
              <div className="message-role">Assistant</div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          {error && (
            <div className="message message-assistant">
              <div className="message-role">Error</div>
              <div className="message-content">
                <div
                  style={{
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    padding: '12px',
                    color: '#c00',
                  }}
                >
                  <strong>⚠️ Error:</strong> {error.message || 'An error occurred while processing your request.'}
                  {error.message?.includes('Input is too long') && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                      💡 Try asking a simpler question or starting a new conversation.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="chat-input-form">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            className="chat-input"
            disabled={status !== 'ready' && !error}
            aria-label="Chat message input"
          />
          <button type="submit" className="chat-submit" disabled={(status !== 'ready' && !error) || !input.trim()}>
            Send
          </button>
        </form>
      </div>

      {/* Saved Queries Sidebar */}
      <SavedQueriesSidebar
        queries={savedQueries}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onSelectQuery={handleLoadQuery}
        onExecuteQuery={handleExecuteQuery}
        onDeleteQuery={handleDeleteQuery}
        isLoading={loadingSavedQueries}
      />

      {/* Save Query Modal */}
      {showSaveModal && (
        <SaveQueryModal
          query={queryToSave}
          datacenter={selectedDatacenter}
          onSave={handleSaveQueryConfirm}
          onCancel={() => setShowSaveModal(false)}
        />
      )}
    </>
  );
};
